import React from 'react';
import Card from '../../ui/Card/Card';
import CurrencyAmount from '../../ui/CurrencyAmount/CurrencyAmount';
import Badge from '../../ui/Badge/Badge';
import BillingDisplayRows from '../BillingDisplayRows/BillingDisplayRows';
import { receiptPaymentCopy } from '../../../utils/statusCopy';
import { getBillingDisplay, resolveBillingDisplay } from '../../../utils/billingDisplay';
import { formatSessionDateTime } from '../../../utils/formatDate';
import './ReceiptSummary.less';

/**
 * Session receipt.
 *
 * Every displayed value comes from the authenticated
 * `/api/user/sessions/:uid` DTO via `adaptAccountSession`. Nothing on
 * this page is computed client-side.
 *
 * The billing block — session type, amounts, who is billed, billing
 * status — is the backend's `billing.display`, rendered row for row.
 * This component does not decide what kind of session it was. Without
 * `billing` (older API) it shows the recorded amount as "Session
 * amount" and the payment state as recorded.
 */

function Row({ label, value, muted = false, emphasis = false }) {
  return (
    <div className={`gc-receipt-row${muted ? ' is-muted' : ''}${emphasis ? ' is-emph' : ''}`}>
      <span className="gc-receipt-row-label">{label}</span>
      <span className="gc-receipt-row-value">{value}</span>
    </div>
  );
}

// Rate-unit label used both in the "Rate" row and (when applicable)
// alongside the numeric rate value.
function rateUnitLabel(unit) {
  if (unit === 'min') return 'min';
  if (unit === 'session') return 'session';
  return 'kWh';
}

function ReceiptSummary({ session }) {
  if (!session) return null;
  const currency = session.currency || 'BBD';
  const captured = session.capturedCents ?? null;
  const authorized = session.authorizedCents ?? null;
  const creditCents = session.creditAppliedCents ?? 0;
  const paymentStatus = session.paymentStatus || null;

  // Rate row inputs. `billingUnit` is the source of truth. When it's
  // null (legacy row), fall back to whichever rate value is populated
  // and label with the matching unit.
  const declaredUnit = session.billingUnit || null;
  const inferredUnit =
    declaredUnit
    || (session.ratePerMinute != null ? 'min' : null)
    || (session.ratePerKwh != null ? 'kwh' : null);
  const rateValue =
    inferredUnit === 'min'
      ? session.ratePerMinute
      : inferredUnit === 'kwh'
      ? session.ratePerKwh
      : null;

  // Billing block. The fallback only applies when the response has no
  // `billing`: the recorded amount and the recorded payment state.
  const billing = resolveBillingDisplay(session, {
    amount: <CurrencyAmount cents={captured} currency={currency} />,
    status: receiptPaymentCopy({
      billingMode: session.billingMode || null,
      paymentStatus,
      capturedCents: captured,
      creditAppliedCents: creditCents,
      totalCents: captured,
    }),
  });

  const display = getBillingDisplay(session);

  // The session row's rate and fee are the tariff recorded at the
  // charger. With `billing` the charge is the server's stored
  // components (`chargeRows`), which may be priced by an agreement the
  // row's rate knows nothing about — so the row's rate and fee are
  // printed only without `billing`. Either way they are recorded
  // values; nothing here is multiplied. A zero rate or fee is
  // suppressed — the receipt never prints a misleading zero.
  const showRate =
    !display && rateValue != null && Number(rateValue) > 0 && inferredUnit !== 'session';
  const showSessionFee =
    !display && session.sessionFee != null && Number(session.sessionFee) > 0;
  const showCredit = creditCents > 0;

  // Card notes describe a card capture, so they need one on record:
  // the server's "Driver paid" above zero, or — without `billing` — a
  // captured amount above zero that the credit did not fully cover.
  const cardCaptured = display
    ? Number(display.driverPaid?.minor) > 0
    : captured != null && captured > 0 && !(creditCents > 0 && creditCents >= captured);
  const showAuthNote =
    cardCaptured && authorized != null && authorized !== captured;

  return (
    <Card className="gc-receipt">
      <header className="gc-receipt-head">
        <div>
          <p className="gc-receipt-eyebrow">Session receipt</p>
          <h2 className="gc-receipt-station">
            {session.stationName || 'Charging session'}
          </h2>
          {session.stationLocation && (
            <p className="gc-receipt-sub">{session.stationLocation}</p>
          )}
        </div>
        <Badge tone={billing.status.tone}>{billing.status.label}</Badge>
      </header>

      <div className="gc-receipt-block">
        <Row label="Started" value={formatSessionDateTime(session.startedAt)} />
        <Row label="Ended" value={formatSessionDateTime(session.endedAt)} />
        {(() => {
          // Prefer the historical snapshot captured at session start
          // — it reflects the plate the driver actually used, even if
          // the saved vehicle has since been edited or removed. Falls
          // back to the legacy plateRaw for pre-migration sessions,
          // then to "Not recorded" so old rows read cleanly.
          const plate = session.licensePlateSnapshot
            || session.license_plate_snapshot
            || session.plateRaw
            || null;
          return plate
            ? <Row label="Vehicle" value={plate} />
            : <Row label="Vehicle" value="Not recorded" />;
        })()}
      </div>

      <div className="gc-receipt-block">
        <Row
          label="Energy delivered"
          value={typeof session.energyKwh === 'number'
            ? `${session.energyKwh.toFixed(2)} kWh`
            : '—'}
        />
        {showRate && (
          <Row
            label={`Rate (${currency}/${rateUnitLabel(inferredUnit)})`}
            value={<CurrencyAmount value={Number(rateValue)} currency={currency} />}
            muted
          />
        )}
        {showSessionFee && (
          <Row
            label="Session fee"
            value={<CurrencyAmount value={Number(session.sessionFee)} currency={currency} />}
            muted
          />
        )}
        {/* Charge components as the backend stored them. */}
        {billing.chargeRows.map((row) => (
          <Row key={row.key} label={row.label} value={row.value} muted />
        ))}
        {showCredit && (
          <Row
            label="Credit applied"
            value={<>−<CurrencyAmount cents={creditCents} currency={session.creditCurrency || currency} /></>}
            muted
          />
        )}
      </div>

      <section className="gc-receipt-billing" aria-label="Billing">
        {billing.summary && (
          <p className="gc-receipt-billing-summary">{billing.summary}</p>
        )}
        <BillingDisplayRows view={billing} />
      </section>

      {showAuthNote && (
        <p className="gc-receipt-note">
          {' '}Pre-authorised
          <CurrencyAmount cents={authorized} currency={currency} className="gc-receipt-note-amt" />;
          only the final amount was captured.
        </p>
      )}

      {session.paymentLast4 && cardCaptured && (
        <p className="gc-receipt-note">Paid with card ending {session.paymentLast4}.</p>
      )}
    </Card>
  );
}

export default ReceiptSummary;
